import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";

const positiveInt = (value, fallback = 0) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const escapeSelectorValue = (value) =>
  window.CSS?.escape ? CSS.escape(value) : value.replace(/"/g, '\\"');

class AddonSuitePrice extends Component {
  guardedForms = new Set();
  // Defaults to 1 (a no-op multiplier) everywhere this component is used without a quantity
  // selector. Only changes when a "quantity-changed" event is received (e.g. the Single/Pair
  // selector used on the single-pair earrings template).
  quantity = 1;

  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest(".shopify-section, dialog") || document;
    this.section.addEventListener(ThemeEvents.variantUpdate, this.updateVariant);
    this.section.addEventListener("addon-pdp:change", this.updateAddons);
    this.section.addEventListener("quantity-changed", this.updateQuantity);
    this.syncFormSubmitGuards();
    this.updateTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.updateVariant);
    this.section?.removeEventListener("addon-pdp:change", this.updateAddons);
    this.section?.removeEventListener("quantity-changed", this.updateQuantity);
    this.guardedForms.forEach((form) => {
      form.removeEventListener("submit", this.preventZeroPriceSubmit, true);
    });
    this.guardedForms.clear();
  }

  updateQuantity = (event) => {
    const parsed = parseInt(event.detail?.quantity, 10);
    this.quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    this.announceNextTotal = true;
    this.updateTotal();
  };

  updateVariant = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector(
      `addon-suite-price[data-block-id="${this.dataset.blockId}"]`,
    );

    if (newPrice instanceof HTMLElement) {
      this.dataset.currentVariantPrice = newPrice.dataset.currentVariantPrice || "0";
      this.dataset.productId = newPrice.dataset.productId || this.dataset.productId;
    } else if (Number.isFinite(event.detail.resource?.price)) {
      this.dataset.currentVariantPrice = String(event.detail.resource.price);
    }

    this.announceNextTotal = true;
    this.updateTotal();
  };

  updateAddons = (event) => {
    const root = event.detail?.root;
    if (
      root instanceof HTMLElement &&
      this.dataset.productId &&
      root.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    this.announceNextTotal = true;
    this.updateTotal();
  };

  updateTotal() {
    const total = (this.currentVariantPrice + this.selectedAddonsPrice) * this.quantity;
    const output = this.querySelector("[data-addon-suite-price-output]");
    if (output) output.textContent = this.formatPrice(total);
    this.hidden = total <= 0;
    this.updatePurchaseState(total);
    this.announceTotal(total);
  }

  // Mirrors the visible total into a polite status region.
  //
  // Gated on `announceNextTotal`, which only the variant / add-on / quantity handlers
  // set. An earlier version skipped the *first* call instead, but updateTotal runs more
  // than once during hydration, so that heuristic was consumed before any interaction
  // and the page-load price was announced.
  announceTotal(total) {
    const announcer = this.querySelector("[data-addon-suite-price-announcer]");
    if (!announcer) return;

    if (!this.announceNextTotal) return;
    this.announceNextTotal = false;

    // Verified on the draft theme: Horizon dispatches a variant:update during hydration,
    // so gating on the handlers alone still let the page-load price through. Seed the
    // last-announced value on the first call and only speak an actual change. This also
    // keeps a same-price variant switch (e.g. metal) silent, which is what we want.
    if (this.lastAnnouncedTotal === undefined) {
      this.lastAnnouncedTotal = total;
      return;
    }
    if (total === this.lastAnnouncedTotal) return;
    this.lastAnnouncedTotal = total;

    if (total <= 0) {
      announcer.textContent = "";
      return;
    }

    const label = announcer.dataset.priceLabel;
    const formatted = this.formatPrice(total);
    const next = label ? `${label}: ${formatted}` : formatted;

    // Re-writing identical text does not reliably re-announce, and announcing an
    // unchanged price on an unrelated variant switch is just noise.
    if (announcer.textContent === next) return;

    announcer.textContent = next;
  }

  get currentVariantPrice() {
    if (!this.includesParentPrice) return 0;
    return positiveInt(this.dataset.currentVariantPrice, 0);
  }

  get includesParentPrice() {
    return this.dataset.includeParentPrice !== "false";
  }

  get selectedAddonsPrice() {
    const productId = this.dataset.productId;
    const scope = this.section || document;
    const roots = productId
      ? [
          ...scope.querySelectorAll(
            `[data-addon-pdp-root][data-product-id="${escapeSelectorValue(productId)}"]`,
          ),
        ]
      : [...scope.querySelectorAll("[data-addon-pdp-root]")];

    return roots.reduce((total, root) => {
      const groups = root.querySelectorAll("[data-addon-group]");
      groups.forEach((group) => {
        total += positiveInt(group.dataset.addonSelectedVariantPrice, 0);
      });
      return total;
    }, 0);
  }

  formatPrice(value) {
    return formatMoney(
      value,
      this.dataset.moneyFormat || "${{amount}}",
      this.dataset.currency || "USD",
    );
  }

  syncFormSubmitGuards() {
    this.getProductForms().forEach((form) => {
      if (this.guardedForms.has(form)) return;
      form.addEventListener("submit", this.preventZeroPriceSubmit, true);
      this.guardedForms.add(form);
    });
  }

  preventZeroPriceSubmit = (event) => {
    if (this.currentVariantPrice + this.selectedAddonsPrice > 0) return;
    event.preventDefault();
    event.stopPropagation();
  };

  updatePurchaseState(total) {
    const shouldDisable = total <= 0;

    this.syncFormSubmitGuards();

    this.getAddToCartButtons().forEach((button) => {
      if (shouldDisable) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.addonSuiteZeroPriceDisabled = "true";
        }
      } else if (button.dataset.addonSuiteZeroPriceDisabled === "true") {
        button.disabled = false;
        delete button.dataset.addonSuiteZeroPriceDisabled;
      }
    });
  }

  getProductForms() {
    const productId = this.dataset.productId;
    const scope = this.section || document;
    if (!productId) return [];

    return [
      ...scope.querySelectorAll(
        `product-form-component[data-product-id="${escapeSelectorValue(productId)}"] form`,
      ),
    ];
  }

  getAddToCartButtons() {
    const productId = this.dataset.productId;
    const scope = this.section || document;
    if (!productId) return [];

    return [
      ...scope.querySelectorAll(
        `product-form-component[data-product-id="${escapeSelectorValue(productId)}"] [ref="addToCartButton"]`,
      ),
      ...scope.querySelectorAll(
        `sticky-add-to-cart[data-product-id="${escapeSelectorValue(productId)}"] [ref="addToCartButton"]`,
      ),
    ].filter(
      (button) =>
        button instanceof HTMLButtonElement || button instanceof HTMLInputElement,
    );
  }
}

if (!customElements.get("addon-suite-price")) {
  customElements.define("addon-suite-price", AddonSuitePrice);
}
